import React from "react";
import PropTypes from "prop-types";
import { ChevronRight, SquareCheckBig } from "lucide-react";

import { UICard } from "../../ui";
import { clx } from "../../../utils/Helpers";
import DashboardEmptyState from "../DashboardEmptyState";
import "../dashboardSection.scss";
import "./QuickActions.scss";

const formatCount = (value) => Number(value).toLocaleString("en-US");

/**
 * Admin dashboard "Quick actions" — one row per pending-request type (icon,
 * name, short description, pending count, chevron); selecting a row opens
 * that request type's screen. A type with 0 pending still shows its row —
 * the section only switches to the (green) "all caught up" empty state when
 * every type is at 0. No header action — there's no single "all requests"
 * screen yet for it to open.
 */
const QuickActions = ({ title, items, emptyTitle, emptyDescription }) => {
  const hasPending = items.some((item) => Number(item.count) > 0);

  return (
    <UICard title={title} className="dashboard-card quick-actions">
      {hasPending ? (
        <ul className="quick-actions__grid">
          {items.map(({ id, icon: Icon, label, description, count, onClick }) => (
            <li key={id}>
              <button
                type="button"
                className="quick-actions__row"
                onClick={onClick}
              >
                <span
                  className={clx("quick-actions__icon", `quick-actions__icon--${id}`)}
                  aria-hidden="true"
                >
                  <Icon size={18} strokeWidth={1.75} />
                </span>
                <span className="quick-actions__copy">
                  <span className="quick-actions__label">{label}</span>
                  <span className="quick-actions__description">
                    {description}
                  </span>
                </span>
                <span className="quick-actions__count">
                  {formatCount(count)}
                </span>
                <ChevronRight
                  size={16}
                  strokeWidth={2.5}
                  className="quick-actions__chevron"
                  aria-hidden="true"
                />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <DashboardEmptyState
          tone="success"
          icon={<SquareCheckBig />}
          title={emptyTitle}
          description={emptyDescription}
        />
      )}
    </UICard>
  );
};

QuickActions.propTypes = {
  title: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      icon: PropTypes.elementType.isRequired,
      label: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      count: PropTypes.number.isRequired,
      onClick: PropTypes.func.isRequired,
    }),
  ),
  emptyTitle: PropTypes.string.isRequired,
  emptyDescription: PropTypes.string.isRequired,
};

QuickActions.defaultProps = {
  items: [],
};

export default QuickActions;
